/**
 * แปลง decimal string เป็นข้อความสำหรับแสดงผล
 *
 * ทุกฟังก์ชันรับ `null` ได้และคืน `—` เสมอ — `null` แปลว่า "คำนวณไม่ได้"
 * ไม่ใช่ศูนย์ การแสดง 0 แทนจะทำให้ผู้ใช้เข้าใจผิดว่ารู้ค่าแล้ว (spec Section 9.3)
 */

export const EM_DASH = '—';

function toNumber(decimalString: string | null | undefined): number | null {
  if (decimalString === null || decimalString === undefined) {
    return null;
  }
  const value = Number(decimalString);
  return Number.isFinite(value) ? value : null;
}

/** จำนวนเงินบาท เช่น `฿1,234,567.89` */
export function formatTHB(decimalString: string | null | undefined): string {
  const value = toNumber(decimalString);
  if (value === null) {
    return EM_DASH;
  }
  return `฿${value.toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** จำนวนเงินพร้อมเครื่องหมายนำหน้าเสมอ เช่น `+฿134,567.89` */
export function formatSignedTHB(decimalString: string | null | undefined): string {
  const value = toNumber(decimalString);
  if (value === null) {
    return EM_DASH;
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatTHB(decimalString)}`;
}

/** เปอร์เซ็นต์พร้อมเครื่องหมาย เช่น `+12.50%` */
export function formatPercent(decimalString: string | null | undefined): string {
  const value = toNumber(decimalString);
  if (value === null) {
    return EM_DASH;
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * เปอร์เซ็นต์แบบไม่มีเครื่องหมาย เช่น `63.13%`
 *
 * ใช้กับสัดส่วนของพอร์ต ซึ่งไม่มีทิศทางบวกลบ — ต่างจาก `formatPercent`
 * ที่ใส่ `+` ให้เพราะกำไรขาดทุนต้องอ่านออกทันทีว่าไปทางไหน
 */
export function formatSharePercent(decimalString: string | null | undefined): string {
  const value = toNumber(decimalString);
  return value === null ? EM_DASH : `${value.toFixed(2)}%`;
}

/** จำนวนหน่วย — ทศนิยมสูงสุด 8 ตำแหน่ง ตัดศูนย์ท้ายทิ้ง */
export function formatQuantity(decimalString: string): string {
  const value = toNumber(decimalString);
  if (value === null) {
    return decimalString;
  }
  return value.toLocaleString('th-TH', { maximumFractionDigits: 8 });
}

/** ราคาในสกุลของมันเอง เช่น `708.01 USD` */
export function formatPrice(
  decimalString: string | null | undefined,
  currency: string | null,
): string {
  const value = toNumber(decimalString);
  if (value === null) {
    return EM_DASH;
  }
  const amount = value.toLocaleString('th-TH', { maximumFractionDigits: 8 });
  return currency === null ? amount : `${amount} ${currency}`;
}

/** จำนวนเงินในสกุลของมันเองพร้อมเครื่องหมาย เช่น `+200 USD` — ใช้กับกำไรที่ยังไม่แปลงเป็นบาท */
export function formatSignedAmount(decimalString: string, currency: string): string {
  const value = toNumber(decimalString);
  if (value === null) {
    return `${decimalString} ${currency}`;
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('th-TH', { maximumFractionDigits: 8 })} ${currency}`;
}

/** วันที่ตามเวลาไทย — เวลาในระบบเป็น UTC เสมอ แปลงเฉพาะตอนแสดงผล */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium' });
}

/** เวลาแบบ `14:32` ตามเขตเวลาของเครื่องผู้ใช้ */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
}

/** class สีตามทิศทางของกำไรขาดทุน — spec Section 9.3 */
export function toneClassFor(decimalString: string | null | undefined): string {
  const value = toNumber(decimalString);
  if (value === null || value === 0) {
    return 'text-ink-400';
  }
  return value > 0 ? 'text-up' : 'text-down';
}
